/**
 * ICU-lite message formatter (W1-12 ui-kit-i18n; ARCHITECTURE.md §13, contract src/i18n/types.ts).
 *
 * Supported syntax (a subset of ICU MessageFormat, no HTML):
 *   {name}                                   interpolation (numbers are written with String(), no grouping)
 *   {n, number}                              locale number formatting (Intl.NumberFormat)
 *   {n, number, integer} / {n, number, percent}
 *   {n, number, 0.00} / {n, number, #,##0.#} decimal patterns: '0' = required digit, '#' = optional digit,
 *                                            ',' in the integer part = grouping
 *   {count, plural, =0 {none} one {# note} other {# notes}}   cardinal plural (Intl.PluralRules), `offset:N`
 *   {n, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}  ordinal plural
 *   {kind, select, tap {Tap} other {Note}}   select on a string value
 *   #                                        inside a plural branch: the plural number (minus offset), formatted
 *   '{' '}' '#' ''                           apostrophe quoting as in ICU: '' is a literal apostrophe, a quote
 *                                            before a syntax character starts a literal run until the next quote
 *
 * Messages are parsed once and cached by source text. Parse errors throw `IcuSyntaxError` (with the character
 * position); `formatMessage` callers decide how to report them (the runtime falls back to the raw text).
 */

import type { Locale } from '../core/util/types';

export type NumberStyle =
  | { readonly kind: 'default' }
  | { readonly kind: 'integer' }
  | { readonly kind: 'percent' }
  | {
      readonly kind: 'pattern';
      readonly minInt: number;
      readonly minFrac: number;
      readonly maxFrac: number;
      readonly grouping: boolean;
    };

export type IcuNode =
  | { readonly t: 'text'; readonly v: string }
  | { readonly t: 'arg'; readonly name: string }
  | { readonly t: 'number'; readonly name: string; readonly style: NumberStyle }
  | {
      readonly t: 'plural';
      readonly name: string;
      readonly ordinal: boolean;
      readonly offset: number;
      readonly options: ReadonlyMap<string, readonly IcuNode[]>;
    }
  | { readonly t: 'select'; readonly name: string; readonly options: ReadonlyMap<string, readonly IcuNode[]> }
  | { readonly t: 'pound' };

export type IcuParams = Readonly<Record<string, string | number>>;

/** Malformed message text; `pos` is the 0-based character index where parsing failed. */
export class IcuSyntaxError extends Error {
  readonly pos: number;
  constructor(message: string, pos: number) {
    super(`${message} (at character ${pos + 1})`);
    this.name = 'IcuSyntaxError';
    this.pos = pos;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------------------------------------------

const MAX_DEPTH = 8;
const IDENT_RE = /[A-Za-z0-9_]/;
const SYNTAX_CHARS = new Set(['{', '}', '#', '|']);

class Parser {
  private i = 0;
  private readonly s: string;
  constructor(s: string) {
    this.s = s;
  }

  parse(): IcuNode[] {
    const nodes = this.message(false, 0);
    if (this.i < this.s.length) throw new IcuSyntaxError('Unexpected "}"', this.i);
    return nodes;
  }

  private peek(): string {
    return this.s[this.i] ?? '';
  }

  private ws(): void {
    while (this.i < this.s.length && /\s/.test(this.peek())) this.i++;
  }

  private expect(ch: string): void {
    if (this.peek() !== ch) {
      throw new IcuSyntaxError(
        this.i >= this.s.length ? `Expected "${ch}" before the end` : `Expected "${ch}"`,
        this.i,
      );
    }
    this.i++;
  }

  /** Text and placeholders until a closing brace (depth > 0) or the end of input. */
  private message(inPlural: boolean, depth: number): IcuNode[] {
    const nodes: IcuNode[] = [];
    let text = '';
    const flush = (): void => {
      if (text !== '') {
        nodes.push({ t: 'text', v: text });
        text = '';
      }
    };
    while (this.i < this.s.length) {
      const c = this.peek();
      if (c === "'") {
        text += this.quoted(inPlural);
        continue;
      }
      if (c === '}') {
        if (depth === 0) throw new IcuSyntaxError('Unexpected "}"', this.i);
        break;
      }
      if (c === '{') {
        flush();
        nodes.push(this.argument(inPlural, depth + 1));
        continue;
      }
      if (c === '#' && inPlural) {
        flush();
        nodes.push({ t: 'pound' });
        this.i++;
        continue;
      }
      text += c;
      this.i++;
    }
    flush();
    return nodes;
  }

  /** Apostrophe handling at `this.i` (which is a quote). Returns the literal text produced. */
  private quoted(inPlural: boolean): string {
    const next = this.s[this.i + 1];
    if (next === "'") {
      this.i += 2;
      return "'";
    }
    if (next !== undefined && SYNTAX_CHARS.has(next) && (next !== '#' || inPlural)) {
      // Literal run until the next single quote ('' inside the run is an apostrophe).
      let out = '';
      this.i++;
      while (this.i < this.s.length) {
        const c = this.peek();
        if (c === "'") {
          if (this.s[this.i + 1] === "'") {
            out += "'";
            this.i += 2;
            continue;
          }
          this.i++;
          return out;
        }
        out += c;
        this.i++;
      }
      return out;
    }
    this.i++;
    return "'";
  }

  private ident(what: string): string {
    const start = this.i;
    while (this.i < this.s.length && IDENT_RE.test(this.peek())) this.i++;
    if (this.i === start) throw new IcuSyntaxError(`Expected ${what}`, this.i);
    return this.s.slice(start, this.i);
  }

  private argument(inPlural: boolean, depth: number): IcuNode {
    if (depth > MAX_DEPTH) throw new IcuSyntaxError('Message nests too deeply', this.i);
    const open = this.i;
    this.expect('{');
    this.ws();
    const name = this.ident('an argument name');
    this.ws();
    if (this.peek() === '}') {
      this.i++;
      return { t: 'arg', name };
    }
    if (this.i >= this.s.length) throw new IcuSyntaxError('Expected "}" before the end', this.i);
    this.expect(',');
    this.ws();
    const type = this.ident('an argument type (number, plural, selectordinal or select)');
    this.ws();
    switch (type) {
      case 'number': {
        let style: NumberStyle = { kind: 'default' };
        if (this.peek() === ',') {
          this.i++;
          const start = this.i;
          while (this.i < this.s.length && this.peek() !== '}') this.i++;
          style = parseNumberStyle(this.s.slice(start, this.i).trim(), start);
        }
        this.ws();
        this.expect('}');
        return { t: 'number', name, style };
      }
      case 'plural':
      case 'selectordinal': {
        this.expect(',');
        this.ws();
        let offset = 0;
        if (this.s.startsWith('offset:', this.i)) {
          this.i += 'offset:'.length;
          this.ws();
          const start = this.i;
          while (this.i < this.s.length && /[0-9]/.test(this.peek())) this.i++;
          if (start === this.i) throw new IcuSyntaxError('Expected a number after "offset:"', this.i);
          offset = Number(this.s.slice(start, this.i));
          this.ws();
        }
        const options = this.options(true, depth, true);
        return { t: 'plural', name, ordinal: type === 'selectordinal', offset, options };
      }
      case 'select': {
        this.expect(',');
        this.ws();
        const options = this.options(inPlural, depth, false);
        return { t: 'select', name, options };
      }
      default:
        throw new IcuSyntaxError(`Unknown argument type "${type}"`, open + 1);
    }
  }

  /** `key {message} key {message} ... }` for plural/select; requires an `other` branch. */
  private options(inPlural: boolean, depth: number, plural: boolean): ReadonlyMap<string, readonly IcuNode[]> {
    const options = new Map<string, readonly IcuNode[]>();
    for (;;) {
      this.ws();
      if (this.peek() === '}') {
        if (!options.has('other')) throw new IcuSyntaxError('Missing the "other" branch', this.i);
        this.i++;
        return options;
      }
      if (this.i >= this.s.length) throw new IcuSyntaxError('Expected "}" before the end', this.i);
      const keyStart = this.i;
      let key: string;
      if (plural && this.peek() === '=') {
        this.i++;
        const start = this.i;
        while (this.i < this.s.length && /[0-9.]/.test(this.peek())) this.i++;
        if (start === this.i) throw new IcuSyntaxError('Expected a number after "="', this.i);
        key = `=${String(Number(this.s.slice(start, this.i)))}`;
      } else {
        key = this.ident('a branch name');
      }
      if (options.has(key)) throw new IcuSyntaxError(`Duplicate branch "${key}"`, keyStart);
      this.ws();
      this.expect('{');
      const body = this.message(inPlural, depth);
      this.expect('}');
      options.set(key, body);
    }
  }
}

function parseNumberStyle(src: string, at: number): NumberStyle {
  if (src === '') return { kind: 'default' };
  if (src === 'integer') return { kind: 'integer' };
  if (src === 'percent') return { kind: 'percent' };
  if (!/^[#0,]*0?(\.[0#]*)?$/.test(src) || !/[0#]/.test(src)) {
    throw new IcuSyntaxError(`Unknown number style "${src}"`, at);
  }
  const [intPart = '', fracPart = ''] = src.split('.');
  return {
    kind: 'pattern',
    minInt: Math.max(1, (intPart.match(/0/g) ?? []).length),
    minFrac: (fracPart.match(/0/g) ?? []).length,
    maxFrac: fracPart.length,
    grouping: intPart.includes(','),
  };
}

const parseCache = new Map<string, readonly IcuNode[]>();

/** Parses (and caches) a message. Throws IcuSyntaxError on malformed text. */
export function parseMessage(src: string): readonly IcuNode[] {
  const hit = parseCache.get(src);
  if (hit !== undefined) return hit;
  const nodes = needsParsing(src) ? new Parser(src).parse() : [{ t: 'text', v: src } as const];
  parseCache.set(src, nodes);
  return nodes;
}

/** True when the text contains syntax (otherwise it is returned verbatim). */
export function needsParsing(src: string): boolean {
  return src.includes('{') || src.includes('}') || src.includes("'");
}

/** null when the message is well formed, otherwise the syntax error. */
export function validateMessage(src: string): IcuSyntaxError | null {
  try {
    parseMessage(src);
    return null;
  } catch (e) {
    if (e instanceof IcuSyntaxError) return e;
    throw e;
  }
}

/** Names of every argument the message reads (for placeholder checks between locales). */
export function messageArguments(src: string): ReadonlySet<string> {
  const out = new Set<string>();
  const walk = (nodes: readonly IcuNode[]): void => {
    for (const n of nodes) {
      switch (n.t) {
        case 'arg':
        case 'number':
          out.add(n.name);
          break;
        case 'plural':
        case 'select':
          out.add(n.name);
          for (const body of n.options.values()) walk(body);
          break;
        case 'text':
        case 'pound':
          break;
      }
    }
  };
  walk(parseMessage(src));
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Formatter
// ---------------------------------------------------------------------------------------------------------------

const numberFormats = new Map<string, Intl.NumberFormat>();
const pluralRules = new Map<string, Intl.PluralRules>();

function numberFormat(locale: Locale, style: NumberStyle): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(style)}`;
  let f = numberFormats.get(key);
  if (f === undefined) {
    let opts: Intl.NumberFormatOptions;
    switch (style.kind) {
      case 'default':
        opts = { maximumFractionDigits: 3 };
        break;
      case 'integer':
        opts = { maximumFractionDigits: 0 };
        break;
      case 'percent':
        opts = { style: 'percent', maximumFractionDigits: 2 };
        break;
      case 'pattern':
        opts = {
          minimumIntegerDigits: style.minInt,
          minimumFractionDigits: style.minFrac,
          maximumFractionDigits: style.maxFrac,
          useGrouping: style.grouping,
        };
        break;
    }
    f = new Intl.NumberFormat(locale, opts);
    numberFormats.set(key, f);
  }
  return f;
}

function pluralRule(locale: Locale, ordinal: boolean): Intl.PluralRules {
  const key = `${locale}|${ordinal ? 'o' : 'c'}`;
  let r = pluralRules.get(key);
  if (r === undefined) {
    r = new Intl.PluralRules(locale, { type: ordinal ? 'ordinal' : 'cardinal' });
    pluralRules.set(key, r);
  }
  return r;
}

/** Formats a number the way `{n, number}` does in `locale`. */
export function formatNumber(n: number, locale: Locale, style: NumberStyle = { kind: 'default' }): string {
  return numberFormat(locale, style).format(n);
}

export interface FormatOptions {
  /** Called once per argument the message reads but `params` does not provide. */
  readonly onMissingParam?: (name: string) => void;
}

function toNumber(v: string | number): number {
  return typeof v === 'number' ? v : Number(v);
}

function render(
  nodes: readonly IcuNode[],
  params: IcuParams,
  locale: Locale,
  pound: number | null,
  opts: FormatOptions,
): string {
  let out = '';
  for (const n of nodes) {
    switch (n.t) {
      case 'text':
        out += n.v;
        break;
      case 'pound':
        out += pound === null ? '#' : formatNumber(pound, locale);
        break;
      case 'arg': {
        const v = params[n.name];
        if (v === undefined) {
          opts.onMissingParam?.(n.name);
          out += `{${n.name}}`;
        } else out += String(v);
        break;
      }
      case 'number': {
        const v = params[n.name];
        if (v === undefined) {
          opts.onMissingParam?.(n.name);
          out += `{${n.name}}`;
        } else {
          const x = toNumber(v);
          out += Number.isNaN(x) ? String(v) : formatNumber(x, locale, n.style);
        }
        break;
      }
      case 'plural': {
        const v = params[n.name];
        if (v === undefined) opts.onMissingParam?.(n.name);
        const x = v === undefined ? NaN : toNumber(v);
        const exact = Number.isNaN(x) ? undefined : n.options.get(`=${String(x)}`);
        let body = exact;
        if (body === undefined) {
          const category = Number.isNaN(x) ? 'other' : pluralRule(locale, n.ordinal).select(x - n.offset);
          body = n.options.get(category) ?? n.options.get('other') ?? [];
        }
        out += render(body, params, locale, Number.isNaN(x) ? null : x - n.offset, opts);
        break;
      }
      case 'select': {
        const v = params[n.name];
        if (v === undefined) opts.onMissingParam?.(n.name);
        const body = (v === undefined ? undefined : n.options.get(String(v))) ?? n.options.get('other') ?? [];
        out += render(body, params, locale, pound, opts);
        break;
      }
    }
  }
  return out;
}

/** Formats `src` with `params` in `locale`. Throws IcuSyntaxError when `src` is malformed. */
export function formatMessage(
  src: string,
  params: IcuParams | undefined,
  locale: Locale,
  opts: FormatOptions = {},
): string {
  if (!needsParsing(src)) return src;
  return render(parseMessage(src), params ?? {}, locale, null, opts);
}
